/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FullscreenIcon from '@mui/icons-material/FullscreenRounded';
import { CircularProgress, IconButton, Paper, Skeleton, Tooltip } from '@mui/material';

import { CIColors, Endpoints, ServiceType } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { Namespaces } from '../../../../i18n';
import { getApiEndpoint } from '../../../../utils/getApiEndpoint';
import { useApplicationTabStyles } from './AppliactionTab.styles';

type Props = {
  serviceType: ServiceType;
  serviceId: string;
};

export const ApplicationTab = memo<Props>(props => {
  const { t } = useTranslation(Namespaces.Cluster);
  const [isLoad, setIsLoad] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const classes = useApplicationTabStyles();

  const { serviceId } = props;

  const src = useMemo(
    () =>
      `${getApiEndpoint(Endpoints.ProxyService.route({ serviceId }))}${props.serviceType === 'cypex' ? '/admin' : ''}`,
    [props.serviceType, serviceId],
  );

  const onLoad = () => {
    setIsLoad(true);
  };

  const onFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <Paper
      elevation={3}
      className={cx(classes.frame, {
        [classes.fullScreen]: isFullscreen,
      })}
      data-cy="scalefield-service-frame"
    >
      {!isLoad && (
        <AlignCenter>
          <CircularProgress color="primary" size={80} />
        </AlignCenter>
      )}
      <Tooltip
        title={isFullscreen ? t('exit_fullscreen_tooltip') : t('enter_fullscreen_tooltip')}
        placement="left"
        PopperProps={{
          disablePortal: true,
          modifiers: [
            {
              name: 'flip',
              enabled: false,
            },
          ],
        }}
      >
        <IconButton
          className="fullscreen-button"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#FFF',
            background: CIColors.GraphicElements,
            borderRadius: '6px',
            visibility: 'hidden',
            transition: 'all .2s ease-in-out',
            '&:hover': {
              color: '#FFF',
              background: CIColors.GraphicElementsHover,
            },
          }}
          onClick={onFullscreen}
          disabled={!props.serviceType}
          data-cy="scalefield-service-frame-fullscreen-button"
        >
          <FullscreenIcon />
        </IconButton>
      </Tooltip>
      {props.serviceType ? (
        <iframe
          title="Embedded application within Scalefield"
          src={src}
          style={{ border: 'none', borderRadius: '6px', width: '100%', height: '100%' }}
          onLoad={onLoad}
          data-cy="scalefield-service-application"
        ></iframe>
      ) : (
        <Skeleton variant="rounded" width="100%" height="100%" />
      )}
    </Paper>
  );
});
